/**
 * Feature 298 — translation for the kitchen display.
 *
 * One namespace (`kds`), catalogues bundled with the code (a kitchen screen
 * must name its buttons with no network). The language: the one chosen on this
 * screen (stored on the device), else the installation's.
 */
import i18next from "i18next";
import { initReactI18next } from "react-i18next";
import { brand } from "../brand";
import {
  FALLBACK_LANGUAGE,
  NAMESPACE,
  STORAGE_KEY,
  SUPPORTED_LANGUAGES,
  initialKdsLanguage,
  isSupportedLanguage,
  type SupportedLanguage,
} from "../lib/i18n/settings";
import itKds from "./locales/it/kds.json";
import enKds from "./locales/en/kds.json";

function storedLanguage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

const initial = initialKdsLanguage(storedLanguage(), brand.defaultLanguage);

void i18next.use(initReactI18next).init({
  lng: initial,
  fallbackLng: FALLBACK_LANGUAGE,
  supportedLngs: [...SUPPORTED_LANGUAGES],
  ns: [NAMESPACE],
  defaultNS: NAMESPACE,
  resources: { it: { [NAMESPACE]: itKds }, en: { [NAMESPACE]: enKds } },
  interpolation: { escapeValue: false, defaultVariables: { brand: brand.productName, kitchenApp: brand.kitchenDisplayName } },
  returnNull: false,
  // A key with no entry renders its own Italian, never the key.
  parseMissingKeyHandler: (key: string, defaultValue?: string) => defaultValue ?? key,
  initImmediate: false,
});

document.documentElement.lang = initial;

/** Chosen in the screen's settings: remembered on this device. */
export function setKdsLanguage(language: SupportedLanguage): void {
  if (!isSupportedLanguage(language)) return;
  try {
    localStorage.setItem(STORAGE_KEY, language);
  } catch {
    /* private mode: lasts for this visit */
  }
  void i18next.changeLanguage(language);
  document.documentElement.lang = language;
}

export { useTranslation, Trans } from "react-i18next";
export default i18next;
